import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../i18n';
import { SlideOver, SlideOverFooter } from '../../components/Shared/SlideOver';
import { Input } from '../../components/Shared/Input';
import { TextArea } from '../../components/Shared/TextArea';
import { useToastStore } from '../../store/useToastStore';
import { selectObjectTasks, useTaskStore } from '../../store/useTaskStore';
import { useEmployeeStore } from '../../store/useEmployeeStore';
import { showApiError } from '../../errors';
import { getPlanEntries } from '../../api/generated/plan-entries/plan-entries';

const { postApiV1PlanningPlanEntriesAdHoc } = getPlanEntries();

interface AdHocPlanEntrySlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  /** When provided, the task picker is filtered to this object. */
  objectId?: string | null;
  /** Called after a successful create (e.g. for refetching plan / list). */
  onCreated?: (planEntryId: string) => void;
}

interface FormState {
  taskId: string;
  employeeId: string;
  date: string;
  durationMinutes: number;
  description: string;
  netPrice: string;
  vatRate: string;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const INITIAL_STATE: FormState = {
  taskId: '',
  employeeId: '',
  date: todayIso(),
  durationMinutes: 60,
  description: '',
  netPrice: '',
  vatRate: '',
};

export function AdHocPlanEntrySlideOver({
  isOpen, onClose, objectId, onCreated,
}: AdHocPlanEntrySlideOverProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const { tasks: allTasks, fetchTasks, fetchObjectTasks } = useTaskStore();
  const objectTasks = useTaskStore(selectObjectTasks(objectId ?? undefined, true));
  const { employees, fetchEmployees } = useEmployeeStore();
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm({ ...INITIAL_STATE, date: todayIso() });
    setErrors({});
    if (objectId) fetchObjectTasks(objectId, { activeOnly: true });
    else fetchTasks();
    fetchEmployees({ status: 'active', pageSize: 200 });
  }, [isOpen, objectId, fetchTasks, fetchObjectTasks, fetchEmployees]);

  const filteredTasks = useMemo(
    () => (objectId ? objectTasks : allTasks.filter((task) => task.isActive !== false)),
    [objectId, objectTasks, allTasks],
  );

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.taskId) next.taskId = t('invoices:adHoc.validation.taskRequired');
    if (!form.employeeId) next.employeeId = t('invoices:adHoc.validation.employeeRequired');
    if (!form.date) next.date = t('invoices:adHoc.validation.dateRequired');
    if (!form.description.trim()) {
      next.description = t('invoices:adHoc.validation.descriptionRequired');
    } else if (form.description.length > 2000) {
      next.description = t('invoices:adHoc.validation.descriptionMax');
    }
    const duration = Number(form.durationMinutes);
    if (Number.isNaN(duration) || duration < 0) {
      next.durationMinutes = t('invoices:adHoc.validation.durationMin');
    }
    const price = Number(form.netPrice);
    if (form.netPrice === '' || Number.isNaN(price) || price < 0) {
      next.netPrice = t('invoices:adHoc.validation.netPriceMin');
    }
    if (form.vatRate !== '') {
      const vat = Number(form.vatRate);
      if (Number.isNaN(vat) || vat < 0 || vat > 100) {
        next.vatRate = t('invoices:adHoc.validation.vatRange');
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const response = await postApiV1PlanningPlanEntriesAdHoc({
        taskId: form.taskId,
        employeeId: form.employeeId,
        date: form.date,
        durationMinutes: Number(form.durationMinutes),
        description: form.description.trim(),
        netPrice: Number(form.netPrice),
        vatRate: form.vatRate !== '' ? Number(form.vatRate) : null,
      });
      useToastStore.getState().addToast(
        t('invoices:adHoc.messages.created'),
        'success',
      );
      onCreated?.(response.planEntryId ?? '');
      onClose();
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const hasUnsaved =
    form.taskId !== ''
    || form.employeeId !== ''
    || form.description !== ''
    || form.netPrice !== ''
    || form.durationMinutes !== INITIAL_STATE.durationMinutes;

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('invoices:adHoc.title')}
      hasUnsavedChanges={hasUnsaved && !saving}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={handleSubmit}
          confirmText={t('invoices:adHoc.actions.create')}
          cancelText={t('common:actions.cancel')}
          loading={saving}
        />
      }
    >
      <p className="text-sm text-text-secondary dark:text-slate-400 mb-4">
        {t('invoices:adHoc.subtitle')}
      </p>

      {objectId && filteredTasks.length === 0 && (
        <div className="mb-4 p-3 rounded-md bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 text-sm">
          {t('invoices:adHoc.hint.noTaskForObject')}
        </div>
      )}

      <div className="space-y-4">
        <FormField label={t('invoices:adHoc.fields.task')} error={errors.taskId} required>
          <select
            value={form.taskId}
            onChange={(e) => setField('taskId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('invoices:adHoc.placeholders.task')}</option>
            {filteredTasks.length === 0 ? (
              <option disabled value="">
                {t('invoices:adHoc.placeholders.noTasks')}
              </option>
            ) : (
              filteredTasks.map((task) => (
                <option key={task.taskId} value={task.taskId}>
                  {task.name} — {task.objectName}
                </option>
              ))
            )}
          </select>
        </FormField>

        <FormField label={t('invoices:adHoc.fields.employee')} error={errors.employeeId} required>
          <select
            value={form.employeeId}
            onChange={(e) => setField('employeeId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('invoices:adHoc.placeholders.employee')}</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('invoices:adHoc.fields.date')} error={errors.date} required>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setField('date', e.target.value)}
            />
          </FormField>

          <FormField
            label={t('invoices:adHoc.fields.durationMinutes')}
            error={errors.durationMinutes}
            required
          >
            <Input
              type="number"
              min={0}
              value={form.durationMinutes}
              onChange={(e) =>
                setField('durationMinutes', parseInt(e.target.value, 10) || 0)
              }
            />
          </FormField>
        </div>

        <TextArea
          label={t('invoices:adHoc.fields.description')}
          required
          rows={3}
          maxLength={2000}
          value={form.description}
          placeholder={t('invoices:adHoc.placeholders.description')}
          onChange={(e) => setField('description', e.target.value)}
          error={errors.description}
        />

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label={t('invoices:adHoc.fields.netPrice')}
            error={errors.netPrice}
            required
          >
            <Input
              type="number"
              min={0}
              step="0.01"
              value={form.netPrice}
              placeholder={t('invoices:adHoc.placeholders.netPrice')}
              onChange={(e) => setField('netPrice', e.target.value)}
            />
          </FormField>

          <FormField label={t('invoices:adHoc.fields.vatRate')} error={errors.vatRate}>
            <Input
              type="number"
              min={0}
              max={100}
              step="0.5"
              value={form.vatRate}
              onChange={(e) => setField('vatRate', e.target.value)}
            />
          </FormField>
        </div>
      </div>
    </SlideOver>
  );
}

export default AdHocPlanEntrySlideOver;

interface FormFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

function FormField({ label, error, required, children }: FormFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
